import React from "react";
import not from "ramda/src/not";

import { IGenderGroups } from "../../services/NetworkApi";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useNetwork } from "../../hooks/useNetwork";
import { useNetworkContext } from "../../contexts/NetworkContext/context";
import useAuth from "../../routes/login/AuthContext";
import { MatchingOptionSwitch } from "./MatchingOptionSwitch";
import Api from "../../services/Api";
import { TableRowEditOptions } from "../TableRowEditOptions";
import { TableRowOptions } from "../TableRowOptions";
import { useModalContext } from "../../contexts/ModalContext/context";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { TableRow } from "../../UI/TableRow";
import { TableCell } from "../../UI/TableCell";
import { TextField } from "../../UI/TextField";
import { GenderGroupDeleteModal } from "./GenderGroupDeleteModal";

interface IProps extends IGenderGroups {
  subnetwork: number;
  isDisabled?: boolean;
}

export const GenderGroupsTableRow: React.FC<IProps> = (props) => {
  const [isEdit, setIsEdit] = React.useState(false);
  const { setIsOpen } = useModalContext();
  const [name, setName] = React.useState(props.name);
  const [isEditLoading, setIsEditLoading] = React.useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = React.useState(false);
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const { selectedNetwork } = useNetwork();
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const { getAuthToken, isSuperAdminRead } = useAuth();

  const toggleEdit = () => setIsEdit(not);
  const toggleDeleteModal = () => setIsOpen(not);

  const handleNameChange = async (e: React.ChangeEvent<{ value: string }>) => {
    setName(e.target.value);
  };

  const handleResponse = ({ success }: { success: boolean }) => {
    if (success) {
      showSuccessSnackbar("Saved Changes");
    } else {
      showErrorSnackbar("An Error Occurred");
    }

    return loadNetworkInfoForNetworks([selectedNetwork.l1Id]);
  };

  const handlChangeByKey = (
    key: "groupCanMatchAll" | "groupCanViewAll" | "groupName"
  ) => async (value: boolean | string) => {
    if (key === "groupCanMatchAll" && value) {
      const res = await Api.setGenderGroup(
        selectedNetwork.l1Id,
        {
          l1Id: selectedNetwork.l1Id,
          l2Id: props.subnetwork,
          id: props.id,
          groupCanMatchAll: true,
          groupCanViewAll: true,
        },
        await getAuthToken()
      );

      return handleResponse(res);
    }

    const res = await Api.setGenderGroup(
      selectedNetwork.l1Id,
      {
        l1Id: selectedNetwork.l1Id,
        l2Id: props.subnetwork,
        id: props.id,
        [key]: value,
      },
      await getAuthToken()
    );

    return handleResponse(res);
  };

  const handlGenderGroupDelete = async () => {
    toggleDeleteModal();
    setIsDeleteLoading(true);
    const res = await Api.deleteGenderGroup(
      selectedNetwork.l1Id,
      props.id,
      await getAuthToken()
    );

    if (res) {
      showSuccessSnackbar("Saved Changes");
    } else {
      showErrorSnackbar("An Error Occurred");
    }

    await loadNetworkInfoForNetworks([selectedNetwork.l1Id]);
    setIsDeleteLoading(false);
  };

  const handleEditConfirm = async () => {
    toggleEdit();
    setIsEditLoading(true);
    await handlChangeByKey("groupName")(name);
    setIsEditLoading(false);
  };

  return (
    <TableRow>
      <TableCell>
        {isEdit && <TextField value={name} onChange={handleNameChange} />}
        {!isEdit && props.name}
      </TableCell>
      <TableCell>
        <ReadOnlyWrapper>
          <MatchingOptionSwitch
            checked={props.groupCanMatchAll}
            onChange={handlChangeByKey("groupCanMatchAll")}
            isDisabled={isSuperAdminRead() || props.isDisabled}
          />
        </ReadOnlyWrapper>
      </TableCell>
      <TableCell>
        <ReadOnlyWrapper>
          <MatchingOptionSwitch
            checked={props.groupCanViewAll}
            isDisabled={(props.groupCanMatchAll || isSuperAdminRead()) || props.isDisabled}
            onChange={handlChangeByKey("groupCanViewAll")}
          />
        </ReadOnlyWrapper>
      </TableCell>
      <TableCell>
        {isEdit && (
          <TableRowEditOptions
            handleCancel={toggleEdit}
            handleConfirm={handleEditConfirm}
          />
        )}
        {!isEdit && (
          <TableRowOptions
            handleEdit={toggleEdit}
            isEditLoading={isEditLoading}
            handleDelete={toggleDeleteModal}
            isDeleteLoading={isDeleteLoading}
            editDisabled={props.isDisabled}
          />
        )}
        <GenderGroupDeleteModal
          group={props.name}
          handleDelete={handlGenderGroupDelete}
        />
      </TableCell>
    </TableRow>
  );
};
